/* ==================================================================
   Hiwe — build-your-own hamper (sections/hiwe-hamper-builder)

   A picker, not a cart. Tapping a tile puts the product in a list;
   the list has a quantity stepper per line; "Send my hamper on
   WhatsApp" is a wa.me link whose ?text= is rebuilt on every change:
   one line per product (title × qty), the estimated products total and
   the shopper's note. Nothing is posted to Shopify.

   Prices are the tiles' data-price (paise, from Liquid), so the total
   is the store's own. Formatting is rupees with Indian grouping — the
   store sells in INR only, and every price on the theme is ₹ + digits.

   State: localStorage 'hiwe-hamper-v1' = { items: {handle: qty}, note },
   every access in try/catch. Handles not on the page are dropped.
   ================================================================== */
(() => {
  'use strict';

  const KEY = 'hiwe-hamper-v1';
  const MAX_QTY = 20;

  const read = () => {
    try {
      const raw = window.localStorage.getItem(KEY);
      const data = raw ? JSON.parse(raw) : null;
      return data && typeof data === 'object' ? data : {};
    } catch (e) {
      return {};
    }
  };
  const write = (data) => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(data));
    } catch (e) {
      /* Storage blocked or full: the list still works for this visit. */
    }
  };

  const rupees = (paise) => {
    const r = paise / 100;
    return '₹' + r.toLocaleString('en-IN', { maximumFractionDigits: r % 1 ? 2 : 0 });
  };

  document.querySelectorAll('[data-hiwe-hamper]').forEach((root) => {
    const tiles = [...root.querySelectorAll('[data-hamper-item]')];
    const byHandle = new Map(tiles.map((t) => [t.dataset.handle, t]));
    const linesEl = root.querySelector('[data-hamper-lines]');
    const emptyEl = root.querySelector('[data-hamper-empty]');
    const sumEl = root.querySelector('[data-hamper-sum]');
    const totalEl = root.querySelector('[data-hamper-total]');
    const noteEl = root.querySelector('[data-hamper-note]');
    const sendEl = root.querySelector('[data-hamper-send]');
    const clearEl = root.querySelector('[data-hamper-clear]');
    const liveEl = root.querySelector('[data-hamper-live]');
    const barEl = root.querySelector('[data-hamper-bar]');
    const countEl = root.querySelector('[data-hamper-count]');
    const barTotalEl = root.querySelector('[data-hamper-bar-total]');
    const wa = (root.dataset.wa || '').replace(/\D/g, '');
    const intro = root.dataset.intro || "Hi Hiwe! I'd like a Diwali hamper with:";
    const outro = root.dataset.outro || '';

    root.classList.add('is-live');

    /* handle -> qty, in the order they were added. */
    const items = new Map();
    const saved = read();
    if (saved.items && typeof saved.items === 'object') {
      Object.entries(saved.items).forEach(([h, q]) => {
        const tile = byHandle.get(h);
        const n = Math.min(MAX_QTY, Math.max(1, parseInt(q, 10) || 1));
        if (tile && !tile.disabled) items.set(h, n);
      });
    }
    if (noteEl && typeof saved.note === 'string') noteEl.value = saved.note.slice(0, 400);

    const announce = (text) => {
      if (!liveEl) return;
      liveEl.textContent = '';
      window.setTimeout(() => { liveEl.textContent = text; }, 30);
    };

    const price = (h) => parseInt(byHandle.get(h)?.dataset.price, 10) || 0;
    const total = () => [...items].reduce((s, [h, q]) => s + price(h) * q, 0);
    const count = () => [...items.values()].reduce((s, q) => s + q, 0);

    const message = () => {
      const lines = [intro];
      items.forEach((q, h) => {
        const t = byHandle.get(h);
        const vendor = t.dataset.vendor ? ` (${t.dataset.vendor})` : '';
        lines.push(`• ${t.dataset.title}${vendor} × ${q}`);
      });
      lines.push('', `Estimated products total: ${rupees(total())}`);
      const note = noteEl ? noteEl.value.trim() : '';
      if (note) lines.push(`Note: ${note}`);
      if (outro) lines.push('', outro);
      return lines.join('\n');
    };

    const save = () => {
      write({ items: Object.fromEntries(items), note: noteEl ? noteEl.value.slice(0, 400) : '' });
    };

    const lineEl = (h, q) => {
      const t = byHandle.get(h);
      const li = document.createElement('li');
      li.className = 'hw-hb__line';
      const name = document.createElement('span');
      name.className = 'hw-hb__line-name';
      name.textContent = t.dataset.title;
      const amt = document.createElement('span');
      amt.className = 'hw-hb__line-amt';
      amt.textContent = rupees(price(h) * q);

      const step = document.createElement('span');
      step.className = 'hw-hb__step';
      const mk = (label, text, delta) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'hw-hb__step-btn';
        b.setAttribute('aria-label', `${label} ${t.dataset.title}`);
        b.textContent = text;
        b.addEventListener('click', () => setQty(h, (items.get(h) || 0) + delta, true));
        return b;
      };
      const n = document.createElement('span');
      n.className = 'hw-hb__step-n';
      n.textContent = String(q);
      n.setAttribute('aria-label', `Quantity ${q}`);
      const plus = mk('One more', '+', 1);
      if (q >= MAX_QTY) plus.disabled = true;
      step.append(mk(q > 1 ? 'One fewer' : 'Remove', q > 1 ? '−' : '×', -1), n, plus);

      li.append(name, step, amt);
      return li;
    };

    let focusHandle = null;
    let focusDir = 0;

    const render = () => {
      const has = items.size > 0;
      tiles.forEach((t) => {
        const on = items.has(t.dataset.handle);
        t.setAttribute('aria-pressed', String(on));
        t.classList.toggle('is-on', on);
      });

      linesEl.replaceChildren(...[...items].map(([h, q]) => lineEl(h, q)));
      emptyEl.hidden = has;
      sumEl.hidden = !has;
      totalEl.textContent = rupees(total());
      if (clearEl) clearEl.hidden = !has;

      if (sendEl) {
        sendEl.setAttribute('aria-disabled', String(!has));
        if (has && wa) sendEl.href = `https://wa.me/${wa}?text=${encodeURIComponent(message())}`;
      }
      if (barEl) {
        barEl.hidden = !has;
        const c = count();
        countEl.textContent = `${c} item${c === 1 ? '' : 's'}`;
        barTotalEl.textContent = rupees(total());
      }

      /* Keep keyboard focus on the stepper the shopper was using. */
      if (focusHandle) {
        const idx = [...items.keys()].indexOf(focusHandle);
        const row = idx >= 0 ? linesEl.children[idx] : null;
        if (row) {
          const btns = row.querySelectorAll('.hw-hb__step-btn');
          const target = focusDir > 0 ? btns[1] : btns[0];
          if (target && !target.disabled) target.focus();
          else if (btns[0]) btns[0].focus();
        } else {
          const tile = byHandle.get(focusHandle);
          if (tile) tile.focus();
        }
        focusHandle = null;
      }
      save();
    };

    const setQty = (h, q, fromStepper) => {
      const t = byHandle.get(h);
      if (!t) return;
      if (fromStepper) {
        focusHandle = h;
        focusDir = q > (items.get(h) || 0) ? 1 : -1;
      }
      if (q <= 0) {
        items.delete(h);
        announce(`Removed ${t.dataset.title}. ${count()} in your hamper.`);
      } else {
        items.set(h, Math.min(MAX_QTY, q));
        announce(`${t.dataset.title}: ${items.get(h)}. ${count()} in your hamper.`);
      }
      render();
    };

    tiles.forEach((t) => {
      t.addEventListener('click', () => {
        if (t.disabled) return;
        const h = t.dataset.handle;
        if (items.has(h)) {
          items.delete(h);
          announce(`Removed ${t.dataset.title}. ${count()} in your hamper.`);
        } else {
          items.set(h, 1);
          announce(`Added ${t.dataset.title}. ${count()} in your hamper.`);
        }
        render();
      });
    });

    if (noteEl) noteEl.addEventListener('input', () => render());

    if (sendEl) {
      sendEl.addEventListener('click', (e) => {
        if (!items.size) {
          e.preventDefault();
          announce('Add at least one product first.');
          const first = tiles.find((t) => !t.disabled);
          if (first) first.focus();
        }
      });
    }
    if (clearEl) {
      clearEl.addEventListener('click', () => {
        items.clear();
        announce('List cleared.');
        render();
        const first = tiles.find((t) => !t.disabled);
        if (first) first.focus();
      });
    }

    render();
  });
})();
